/**
 * NotificationsBell — bell con badge + dropdown panel + SSE realtime.
 *
 * Doc 21 Fase Notif-1.e + Fase 7.h. Comportamiento:
 *
 * - **Badge**: muestra `unread_count` del backend. Polling cada 30s
 *   como fallback al SSE (que es el camino primario para realtime).
 *   El SSE invalida la query de count cuando llega una notif nueva.
 *
 * - **Dropdown**: click abre/cierra el panel. Click fuera lo cierra.
 *   ESC también. El panel renderiza las N notifs más recientes con
 *   `<NotificationsPanel>`; ese componente hace su propia query con
 *   cursor pagination.
 *
 * - **SSE (Fase 7.h)**: el bell no abre la conexión directo. Se
 *   suscribe al `notificationStreamStore` (Zustand singleton) que
 *   mantiene UNA sola conexión por sesión. Esto evita fugas de
 *   conexiones SSE causantes de saturación del connection pool HTTP/1.1
 *   y subsecuentes "hangs" de la UI.
 *
 * - **Sin sesión**: si no hay accessToken, el bell se muestra disabled
 *   (no es el caso típico — los hijos del AuthedShell siempre tienen
 *   user logueado).
 */

import { useEffect, useRef, useState } from 'react'

import { useAccount, useMsal } from '@azure/msal-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { fetchUnreadCount } from '../../api/notifications'
import { apiTokenRequest } from '../../auth/msalConfig'
import { useAccessToken } from '../../auth/useAccessToken'
import { useNotificationStreamStore } from '../../stores/notificationStreamStore'
import { NotificationsPanel } from './NotificationsPanel'

export function NotificationsBell() {
  const { t } = useTranslation('shell')
  const { accessToken } = useAccessToken()
  const { instance, accounts } = useMsal()
  const account = useAccount(accounts[0]) ?? null
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)

  // ─── Unread count (polling + SSE-invalidated) ────────────────────────
  const countQuery = useQuery({
    queryKey: ['me', 'notifications', 'unread_count'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchUnreadCount(accessToken)
    },
    enabled: !!accessToken,
    // Polling fallback cada 30s — si el SSE se cae momentáneamente
    // el badge sigue actualizándose. Cuando llega SSE, invalidamos
    // esta query inmediatamente (ver useEffect abajo).
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  })
  const unreadCount = countQuery.data?.count ?? 0

  // ─── SSE singleton: conectar cuando llega token + suscribirse ────────
  // El `connect` es idempotente — con la conexión viva sólo actualiza
  // cómo pedir el token. El store pide uno nuevo a MSAL en cada intento
  // de reconexión. La conexión vive en el store global, no en este componente.
  const connectStream = useNotificationStreamStore((s) => s.connect)
  const subscribeStream = useNotificationStreamStore((s) => s.subscribe)
  useEffect(() => {
    if (!accessToken || !account) return
    connectStream(
      async () => (await instance.acquireTokenSilent({ ...apiTokenRequest, account })).accessToken,
    )
  }, [accessToken, account, instance, connectStream])

  useEffect(() => {
    const unsubscribe = subscribeStream(() => {
      // Invalida las queries — TanStack Query refetchea automático.
      // No insertamos el item manualmente en cache porque el list
      // tiene cursor pagination y el shape podría desincronizarse;
      // refetch es más simple y barato.
      void queryClient.invalidateQueries({
        queryKey: ['me', 'notifications'],
      })
    })
    return unsubscribe
  }, [subscribeStream, queryClient])

  // ─── Cerrar dropdown al click afuera ────────────────────────────────
  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const disabled = !accessToken
  const hasUnread = unreadCount > 0

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        title={t('notifications.title')}
        aria-label={t('notifications.title')}
        aria-expanded={open}
        className={
          disabled
            ? 'p-2 rounded-md text-ink-muted relative cursor-not-allowed opacity-70'
            : 'p-2 rounded-md text-ink-muted hover:text-ink hover:bg-surface-muted relative transition-colors'
        }
      >
        <Bell className="w-4 h-4" aria-hidden="true" />
        {hasUnread && (
          <span
            className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-high text-canvas text-[9px] font-mono font-semibold flex items-center justify-center"
            aria-label={t('notifications.unread_count', {
              count: unreadCount,
              defaultValue: '{{count}} no leídas',
            })}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && !disabled && (
        <NotificationsPanel onClose={() => setOpen(false)} />
      )}
    </div>
  )
}
